/**
 * ConsultationOffer — domain reading of a carrier's `eligible_offers` payload.
 *
 * The payload rides on quick-action buttons and document messages (per-carrier,
 * not per-thread). This class owns the DECISIONS made over it; carrier
 * *resolution* (which button/document supplies the payload — see
 * `helpers.findeligibleOffers`, QuickActionButtons fallback) stays outside the
 * domain layer on purpose: it is plumbing about provenance, not entitlement.
 *
 * Wire quirks this encodes (previously spread across tierActions,
 * lawyerHandoff, DocumentCard, DocumentPanel, LegalReviewButton):
 * - `lawyer_consultation` may be a string ("default", ...), explicit `null`,
 *   boolean `false`, or absent. Missing payload ≠ empty payload: a missing
 *   payload is out of scope, an empty object is in scope (matches the old
 *   `tierActions.isOutOfScope`).
 * - `consultation_suppression_reason` is a BE-internal debug string: presence
 *   means suppressed; callers must never branch on its value.
 *
 * Roadmap: docs/domain-refactor-roadmap.md (Module 5). Linear: INH-5150.
 */

export interface RawEligibleOffers {
  lawyer_consultation?: string | boolean | null;
  consultation_suppression_reason?: string | null;
  legal_review_title?: string | null;
}

export class ConsultationOffer {
  /** The raw payload, for pass-through to dispatches/analytics. Null when missing. */
  readonly raw: RawEligibleOffers | null;

  private constructor(raw: RawEligibleOffers | null) {
    this.raw = raw;
    Object.freeze(this);
  }

  private static readonly MISSING = new ConsultationOffer(null);

  static from(raw: RawEligibleOffers | null | undefined): ConsultationOffer {
    if (raw == null) return ConsultationOffer.MISSING;
    return new ConsultationOffer(raw);
  }

  /** The whole payload was absent (older threads, /try, V3.5 legal pill). */
  isMissing(): boolean {
    return this.raw === null;
  }

  /**
   * Consultation is in scope for this carrier. Missing payload, explicit
   * `null`, and boolean `false` are all out of scope; anything else —
   * including an empty payload object — is in scope. Exact port of the old
   * `tierActions.isOutOfScope` inverse.
   */
  isAvailable(): boolean {
    if (this.raw === null) return false;
    return this.raw.lawyer_consultation !== null && this.raw.lawyer_consultation !== false;
  }

  /**
   * The BE explicitly declined the offer (`lawyer_consultation: null`).
   * Narrower than `!isAvailable()`: a missing payload is NOT a decline —
   * LegalReviewButton renders differently for the two cases.
   */
  isDeclined(): boolean {
    return this.raw !== null && this.raw.lawyer_consultation === null;
  }

  /** The plain "default" consultation offer (DocumentCard / DocumentPanel gate). */
  isDefault(): boolean {
    return this.raw?.lawyer_consultation === "default";
  }

  /**
   * BE suppressed the consultation CTA. Presence-only check — the reason
   * string is BE debug output and must never be rendered or branched on.
   */
  isSuppressed(): boolean {
    return this.raw?.consultation_suppression_reason != null;
  }

  /** Optional BE-provided subtitle for the legal-review CTA. */
  get title(): string | null {
    return this.raw?.legal_review_title ?? null;
  }
}
